import type {
  RegistrationFormAnswerType,
  RegistrationFormQuestionType,
} from "@/features/eventDetails/components/Context/RegistrationFormContext"
import type { QuestionType, RegistrationStatus, SubscribeRequest } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { isBefore } from "date-fns"
import dynamic from "next/dynamic"
import { useParams } from "next/navigation"
import { useState } from "react"

import { useRegistrationForm } from "@/features/eventDetails/components/Context/RegistrationFormContext"
import { AccessButton } from "@/features/eventDetails/components/RegistrationDetails/AccessButton"
import { AttendanceRequired } from "@/features/eventDetails/components/RegistrationDetails/AttendanceRequired"
import { ManageSlotsButton } from "@/features/eventDetails/components/RegistrationDetails/ManageSlotsButton"
import { SaveButton } from "@/features/eventDetails/components/RegistrationDetails/SaveButton"
import { SubscribeButton } from "@/features/eventDetails/components/RegistrationDetails/SubscribeButton"
import { WaitingListButton } from "@/features/eventDetails/components/RegistrationDetails/WaitingListButton"
import {
  EXTERNAL,
  JOBTEASER,
  NO_SPOTS_LEFT,
  ONLINE,
  REGISTRATION_STATUS_INCOMPLETE,
  REGISTRATION_STATUS_NOT_SUBSCRIBED,
  REGISTRATION_STATUS_SUBSCRIBED,
  REGISTRATION_STATUS_UNSUBSCRIBED,
  REGISTRATION_STATUS_USER_NOT_AVAILABLE,
  WITHOUT_REGISTRATION,
} from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useRegistrationStatus } from "@/modules/events/hooks/useRegistrationStatus"
import { useSubscribe } from "@/modules/events/hooks/useSubscribe"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { prepareModalClosing } from "@/modules/layouts/components/Modal/Modal"
import { useLocale } from "@/modules/locales/useLocale"
import { useNotification } from "@/modules/notification/hooks"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./MainButtonBlock.module.css"

const SubscriptionModal = dynamic(() =>
  import("@/features/eventDetails/components/SubscriptionModal/SubscriptionModal").then(
    component => component.SubscriptionModal
  )
)
const SlotSelectionModal = dynamic(() =>
  import("@/features/eventDetails/components/SlotSelectionModal/SlotSelectionModal").then(
    component => component.SlotSelectionModal
  )
)
const WaitingListConfirmedModal = dynamic(() =>
  import("@/features/eventDetails/components/RegistrationDetails/WaitingListConfirmedModal").then(
    component => component.WaitingListConfirmedModal
  )
)
type MainButtonBlockProps = {
  registrationStatus: RegistrationStatus
}
export const MainButtonBlock: FunctionComponent<MainButtonBlockProps> = ({ registrationStatus }) => {
  const { registrationFormData, setRegistrationFormData } = useRegistrationForm()
  const locale = useLocale()
  const showNotification = useNotification()
  const { t } = useTranslation(["event_details", "shared_error"])
  const trackEvent = useTrackEvent()
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const { dataCurrentUser } = useCurrentUserFetch()
  const { refetch: refetchRegistrationStatus } = useRegistrationStatus({
    eventId: event === undefined ? "" : event.id,
    locale,
  })
  const { mutate: postSubscribe } = useSubscribe()

  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)
  const [isSlotSelectionModalOpen, setIsSlotSelectionModalOpen] = useState(false)
  const [isWaitingListConfirmedModalOpen, setIsWaitingListConfirmedModalOpen] = useState(false)
  const [isSubscriptionButtonLoading, setIsSubscriptionButtonLoading] = useState(false)
  const [isManageSlotsButtonClicked, setIsManageSlotsButtonClicked] = useState(false)

  if (!event) {
    return null
  }

  const {
    endDate: rawEndDate,
    startDate: rawStartDate,
    registrationDetails,
    location,
    id,
    isExclusive,
    ownership,
    careerCenter,
  } = event
  const { type, hasSubevents } = registrationDetails

  const isRegistrationJobteaser = type === JOBTEASER
  const isRegistrationExternal = type === EXTERNAL
  const isWithoutRegistration = type === WITHOUT_REGISTRATION
  const isEventFull = registrationStatus.remainingSpots === NO_SPOTS_LEFT
  const isEventWithAttendanceRequired = isRegistrationJobteaser
    ? registrationDetails.jobteaser?.isAttendanceRequired
    : false
  const isManualRegistrationValidation =
    isRegistrationJobteaser && registrationDetails.jobteaser.isManualRegistrationValidation

  const currentDate = new Date()
  const endDate = new Date(rawEndDate)
  const startDate = new Date(rawStartDate)
  const isEventFinished = isBefore(endDate, currentDate)
  const hasEventNotStarted = isBefore(currentDate, startDate)
  const isRegistrationDeadlineNotExceeded =
    isWithoutRegistration || isBefore(currentDate, new Date(registrationDetails.registrationDeadline))

  const isUserRegistered = registrationStatus.status === REGISTRATION_STATUS_SUBSCRIBED
  const isUserNotRegistered = registrationStatus.status === REGISTRATION_STATUS_NOT_SUBSCRIBED
  const isUserUnsubscribed = registrationStatus.status === REGISTRATION_STATUS_UNSUBSCRIBED
  const isRegistrationIncomplete = registrationStatus.status === REGISTRATION_STATUS_INCOMPLETE
  const canRegister = isUserNotRegistered || isUserUnsubscribed
  const isUserNotAvailable = registrationStatus.status === REGISTRATION_STATUS_USER_NOT_AVAILABLE
  const isCompanyEvent = event && event.ownership.type.toLowerCase() === "company"
  const isSchoolEvent = event && event.ownership.type.toLowerCase() === "school"

  const isStudent = dataCurrentUser?.attributes.isStudent
  const isLogged = dataCurrentUser?.isLogged

  const isDisplayedAccessButton =
    isRegistrationJobteaser && isUserRegistered && location.type === ONLINE && !isEventFinished
  const isDisplayedSubscribeButton =
    (isRegistrationExternal ||
      (isRegistrationJobteaser && canRegister) ||
      (isRegistrationJobteaser && isUserNotAvailable)) &&
    isLogged
  const isDisplayedWaitingListButton =
    isDisplayedSubscribeButton && isRegistrationDeadlineNotExceeded && isEventFull && !hasSubevents
  const isDisplayedSaveButton =
    hasEventNotStarted && isRegistrationDeadlineNotExceeded && isLogged && isUserNotRegistered && isStudent
  const isDisplayedAttendanceRequired =
    isRegistrationJobteaser && isEventWithAttendanceRequired && canRegister && !isEventFull
  const isDisplayedManageSlotsButton =
    hasSubevents && (isRegistrationIncomplete || isUserRegistered) && !isEventFinished && isLogged

  const hasQuestions =
    isRegistrationJobteaser &&
    registrationDetails.jobteaser.questions &&
    registrationDetails.jobteaser.questions.length > 0

  const hasResume = isRegistrationJobteaser && registrationDetails.jobteaser.isCvRequired

  const handleSubscribeButtonClick = (): void => {
    setIsSubscriptionModalOpen(true)
  }

  const handleManageSlotsButtonClick = (): void => {
    setIsManageSlotsButtonClicked(true)
    setIsSlotSelectionModalOpen(true)
  }

  if (
    isEventFinished ||
    (!isDisplayedSubscribeButton &&
      !isDisplayedAccessButton &&
      !isRegistrationIncomplete &&
      !isDisplayedSaveButton &&
      !isDisplayedAttendanceRequired &&
      !isDisplayedManageSlotsButton)
  ) {
    return null
  }
  const successTitle = isManualRegistrationValidation
    ? t("event_details.notifications.subscribe_under_manual_validation.success.title")
    : t("event_details.notifications.subscribe.success.title")
  const successContent = isManualRegistrationValidation
    ? t("event_details.notifications.subscribe_under_manual_validation.success.content")
    : ""

  // check if all questions are answered, and if not, add an error message
  const validateFormQuestions = (questions: RegistrationFormQuestionType[]): RegistrationFormQuestionType[] => {
    const isAnswerValid = (answer: RegistrationFormAnswerType): boolean => {
      return !(answer === null || answer === "" || (Array.isArray(answer) && answer.length === 0))
    }

    const getErrorText = (questionType: QuestionType): string => {
      switch (questionType) {
        case "select_multiple":
          return t("event_details.subscription.form.questions.error.select_minimum_one_option")
        case "select":
        case "boolean":
          return t("event_details.subscription.form.questions.error.select_one_option")
        case "file":
          return t("event_details.subscription.form.questions.error.select_file")
        case "string":
        case "text":
          return t("event_details.subscription.form.questions.error.fill_in_field")
        default:
          return t("event_details.subscription.form.questions.error.fill_in_field")
      }
    }

    return questions.map(question => {
      if (!isAnswerValid(question.answer)) {
        return { ...question, error: getErrorText(question.type) }
      }
      return question
    })
  }

  const validateForm = (): boolean => {
    let isValid = true

    // Validate questions if there are any
    if (hasQuestions) {
      const updatedQuestions = validateFormQuestions(registrationFormData.questions)
      const hasErrors = updatedQuestions.some(question => question.error)

      if (hasErrors) {
        setRegistrationFormData(prevFormData => ({
          ...prevFormData,
          questions: updatedQuestions,
        }))
        isValid = false

        // scroll up to the first error
        const firstErrorQuestion = updatedQuestions.find(question => question.error)
        const scrollElement = document.getElementById(`${firstErrorQuestion?.id || ""}-container`)
        scrollElement?.scrollIntoView({ behavior: "smooth" })
      }
    }

    // check if a resume is provided, and if not, add an error message
    if (hasResume && registrationFormData.resumeType === "tailored_resume") {
      if (!registrationFormData.tailoredResumeId) {
        setRegistrationFormData(prevFormData => ({
          ...prevFormData,
          tailoredResume: {
            ...prevFormData.tailoredResume,
            error: t("event_details.subscription.form.resume.error.select_file"),
          },
        }))
        isValid = false
      }
    }

    // TO-DO: Add other validation checks for profile, search criteria, etc.

    return isValid
  }
  const validateSlots = (): boolean => {
    let isValid = true

    // Check subevent registrations
    const hasErrors = registrationFormData.subEvents.values.find(subevent => subevent.isRegistered) === undefined

    if (hasErrors) {
      setRegistrationFormData(prevFormData => ({
        ...prevFormData,
        subEvents: {
          ...prevFormData.subEvents,
          error: t("event_details.slots.error.slot_required"),
        },
      }))
      isValid = false

      setTimeout(() => {
        const scrollElement = document.getElementById("event-details-slot-modal")
        scrollElement?.scrollIntoView({ behavior: "smooth" })
      })
    }

    return isValid
  }

  const handleStep1Validation = (): void => {
    if (!validateForm()) return
    setIsSubscriptionModalOpen(false)
    setIsSlotSelectionModalOpen(true)
  }

  const handleGoBackToSubscriptionModal = (): void => {
    setIsSubscriptionModalOpen(true)
    setIsSlotSelectionModalOpen(false)
  }

  const handleSubscriptionModalClose = (): void => {
    setIsSubscriptionModalOpen(false)
  }

  const handleSlotModalClose = (): void => {
    prepareModalClosing()
    setIsSlotSelectionModalOpen(false)
    setIsManageSlotsButtonClicked(false)
  }

  const handleWaitingListModalClose = (): void => {
    prepareModalClosing()
    setIsWaitingListConfirmedModalOpen(false)
    refetchRegistrationStatus()
  }

  // handlePostSubscribe can be called by both modals, depending on if the event has slots or not
  const handlePostSubscribe = (formData: FormData): void => {
    setIsSubscriptionButtonLoading(true)
    postSubscribe(
      {
        authenticityToken: event?.authenticityToken,
        formData,
        hostname: window.location.hostname,
        id: event?.id,
        locale,
      } as SubscribeRequest,
      {
        onError: () => {
          setIsSubscriptionModalOpen(false)
          handleSlotModalClose()
          showNotification({
            title: t("shared_error.generic"),
            variant: "error",
          })
          setIsSubscriptionButtonLoading(false)
        },
        onSuccess: () => {
          // track only registrations, not slot selection changes
          if (!isManageSlotsButtonClicked) {
            const [subdomain] = window.location.host.split(".")

            trackEvent("event_ad_subscription", {
              company_id: isCompanyEvent ? ownership.id : "",
              event_ad_id: id,
              is_public: !isExclusive,
              owner_type: ownership.type || "",
              school_id: careerCenter && ((isCompanyEvent && isExclusive) || isSchoolEvent) ? careerCenter.id : "", // null is not accepted as default
              service: "event_details",
              subdomain,
              trackee_school_id: dataCurrentUser?.attributes.careerCenterUuid || "",
            })
          }
          if (!isEventFull) {
            // the event can be full ONCE the user gets the last spot.
            // We don't want to show the WaitingListConfirmedModal unless they trully are part of the waiting list, so we will refetch the status after the modal is closed
            refetchRegistrationStatus()
            showNotification({
              content: successContent,
              title: successTitle,
              variant: "success",
            })
          }
          // if method was called by SlotModal
          if (hasSubevents) {
            handleSlotModalClose()
          } else {
            handleSubscriptionModalClose()
          }
          // if user is added to
          if (isEventFull) {
            setIsWaitingListConfirmedModalOpen(true)
          }
          setIsSubscriptionButtonLoading(false)
          setRegistrationFormData({
            profileResumeId: null,
            questions: [],
            resumeType: null,
            subEvents: {
              error: "",
              values: [],
            },
            tailoredResume: {
              error: "",
              file: null,
            },
            tailoredResumeId: null,
          })
        },
      }
    )
  }

  const onSubmit = (): void => {
    if (isManageSlotsButtonClicked) {
      // no error handling when the user is already registered & just managing their slots
      handleSlotModalClose()
    } else {
      if (!validateForm()) {
        return
      }

      const formData = new FormData()
      formData.append("authenticity_token", event?.authenticityToken as string)
      formData.append(
        "applicant[resume_type]",
        registrationFormData.tailoredResume.file ? "tailored_resume" : "profile_resume"
      )
      if (registrationFormData.resumeType === "tailored_resume") {
        formData.append("applicant[resume_id]", registrationFormData.tailoredResumeId || "")
      }

      if (hasQuestions) {
        registrationFormData.questions.forEach(question => {
          if (question.type === "select_multiple") {
            const answers = question.answer as string[]
            answers.forEach(answer => {
              formData.append(`applicant[answers][${question.id}][]`, answer)
            })
          } else {
            formData.append(`applicant[answers][${question.id}]`, question.answer as string)
          }
        })
      }

      if (hasSubevents) {
        // check if there is at least one selected subevent
        if (!validateSlots()) return

        // extract selected slots from list and add them into request payload
        const selectedSlots = registrationFormData.subEvents.values.filter(subevent => subevent.isRegistered)

        selectedSlots.forEach(subevent => {
          formData.append("applicant[sub_event_ids][]", subevent.id)
        })
      }

      handlePostSubscribe(formData)
    }
  }

  return (
    <div className={styles.buttons} data-testid="event-details-registration-details-main-button-block">
      {isDisplayedAccessButton && <AccessButton registrationStatus={registrationStatus} />}

      {isDisplayedSubscribeButton && (
        <SubscribeButton handleClick={handleSubscribeButtonClick} registrationStatus={registrationStatus} />
      )}
      {isDisplayedWaitingListButton && <WaitingListButton handleClick={handleSubscribeButtonClick} />}
      {isDisplayedManageSlotsButton && <ManageSlotsButton handleClick={handleManageSlotsButtonClick} />}
      {isDisplayedSaveButton && <SaveButton />}
      {isDisplayedAttendanceRequired && <AttendanceRequired />}
      {isSubscriptionModalOpen && (
        <SubscriptionModal
          isEventFull={isEventFull}
          isOpen
          isSubmitDisabled={isSubscriptionButtonLoading}
          isSubmitLoading={isSubscriptionButtonLoading}
          onClose={handleSubscriptionModalClose}
          onNextStep={handleStep1Validation}
          onSubmit={onSubmit}
        />
      )}
      {isSlotSelectionModalOpen && (
        <SlotSelectionModal
          isManageSlotsButtonClicked={isManageSlotsButtonClicked}
          isOpen
          isSubmitDisabled={isSubscriptionButtonLoading}
          isSubmitLoading={isSubscriptionButtonLoading}
          onClose={handleSlotModalClose}
          onGoBack={handleGoBackToSubscriptionModal}
          onSubmit={onSubmit}
        />
      )}
      {isWaitingListConfirmedModalOpen && <WaitingListConfirmedModal onClose={handleWaitingListModalClose} />}
    </div>
  )
}
